import { Button, ManageableItemsList } from '@payaca/ambrosia';
import { FC } from 'react';

import { getInternationalDateFormatByLocale } from '@payaca/helpers/internationalHelper';
import { Deal } from '@payaca/types/dealTypes';

import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';

import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import {
  MaterialsListAuditLog,
  MaterialsListAuditLogsPage,
} from '@/api/queries/project/useGetProjectMaterialsListAuditLogs';

import { DateFormats } from '@payaca/helpers/internationalHelper';
import { roundToDecimalPlaces } from '@payaca/helpers/numberHelper';
import { format } from 'date-fns';
import { useAuditLogsInputPaginationState } from './materialsPanelHooks';

export const HistoryPanel: FC<{
  auditLogs: MaterialsListAuditLogsPage | undefined;
  projectId?: Deal['id'];
  inputPaginationState: ReturnType<typeof useAuditLogsInputPaginationState>;
  onMaterialView: (materialId: string) => void;
}> = (props) => {
  const {
    projectId,
    auditLogs,
    inputPaginationState: {
      pagination,
      input,
      setActingUserIds,
      setSearchTerm,
      setCurrentPage,
    },
    onMaterialView,
  } = props;

  const { data: regionPreferences } = useGetMyRegionPreferences();

  const timestampRegionalFormat = getInternationalDateFormatByLocale(
    DateFormats.TIMESTAMP_SHORT,
    regionPreferences?.locale
  );

  const { data: activeAccountUsers } =
    useGetMyAccountUsersExcludedDeactivated();

  return (
    <div className="@container">
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput onChange={setSearchTerm} />
        <ManageableItemsList.ActionBar.BasicFilter
          options={
            activeAccountUsers?.map((u) => ({
              value: u.id,
              label: u.fullName,
            })) || []
          }
          value={input.actingUserIds || []}
          onChange={setActingUserIds}
        />
      </ManageableItemsList.ActionBar>
      <ManageableItemsList.Table<MaterialsListAuditLog>
        isLoading={!auditLogs}
        items={auditLogs?.items || []}
        uniqueKey={'id'}
      >
        <ManageableItemsList.Table.Column<MaterialsListAuditLog, 'id'>
          header="Material"
          field="id"
          render={(id, row) => (
            <div className="max-w-lg truncate">
              <Button
                variant="link"
                onClick={() => onMaterialView(row.material.id)}
              >
                {row.material.name}
              </Button>
            </div>
          )}
        />

        <ManageableItemsList.Table.Column<MaterialsListAuditLog, 'id'>
          header="Qty change"
          field="id"
          className="text-right"
          render={(id, row) => (
            <>
              {row.quantityChange >= 0 ? '+' : '-'}
              {roundToDecimalPlaces(Math.abs(row.quantityChange), 2)}
            </>
          )}
        />
        <ManageableItemsList.Table.Column<MaterialsListAuditLog, 'id'>
          header="Submitted by"
          field="id"
          render={(id, row) => row.actingUser?.fullName}
        />
        <ManageableItemsList.Table.Column<MaterialsListAuditLog, 'id'>
          header="Submitted at"
          field="id"
          render={(id, row) => format(row.timestamp, timestampRegionalFormat)}
        />
      </ManageableItemsList.Table>
      {auditLogs && (
        <ManageableItemsList.PaginationBar
          type="custom"
          totalItems={auditLogs.totalCount}
          pageSize={auditLogs.limit}
          currentPage={auditLogs.offset / auditLogs.limit + 1}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};
